import styled from '@emotion/styled';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  LayoutDashboard,
  Activity,
  FileText,
  UserCog,
  Network,
  BarChart3,
  AlertCircle,
  Clock,
  Camera,
  ShieldAlert,
  Clipboard,
  Map,
  CalendarOff,
  Globe,
  UserCircle,
  Plane,
  Users,
  Settings,
  Image as ImageIcon,
  Webcam,
  UsersRound,
  Sliders,
  BookOpen,
  ChevronDown,
} from 'lucide-react';
import { UserContext } from '../../contexts/User';
import useWorkspaces from '../../hooks/useWorkspaces';
import WorkspaceSVG from '../../images/workspace.svg';
import './sidebar.css';
import { canAccessNavItem, getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel } from '../../constants/accountTypes';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

const Avatar = styled.div`
  border-radius: 6px;
  width: 32px;
  height: 32px;
  background: var(--dark-7);
  color: var(--dark-3);
  ${(props) => props.src && `
    background-size: cover;
    background-image: url(${props.src});
  `}
`;

// Shared "Chart Abstraction" section — Overview + Team Productivity + QA Queue.
// Visibility is driven by the workspace feature flag + role/QA access in the
// filter below (canAccessNavItem stays permissive: accountLevel 1, no permission).
const chartAbstractionSubmenu = {
  text: 'Chart Abstraction',
  icon: Clipboard,
  enabled: true,
  submenuLocation: '/admin/chart-abstraction',
  accountLevel: 1,
  subRoutes: [
    {
      text: 'Overview',
      routeName: '/admin/chart-abstraction',
      enabled: true,
      exact: true,
      accountLevel: 1,
    },
    {
      text: 'Team Productivity',
      routeName: '/admin/chart-abstraction/productivity',
      enabled: true,
      accountLevel: 1,
    },
    {
      text: 'QA Queue',
      routeName: '/chart-abstraction/qa-queue',
      enabled: true,
      accountLevel: 1,
    },
  ],
};

const linkArrayNamesAdmin = [
  {
    routeName: '/dashboard',
    text: 'Dashboard',
    icon: LayoutDashboard,
    enabled: true,
    permission: 'dashboard:read',
    accountLevel: 1,
  },
  chartAbstractionSubmenu,
  {
    text: 'Team Engagement',
    routeName: '/members/team-engagement',
    icon: Activity,
    enabled: true,
    permission: 'team:read',
    accountLevel: 2,
  },
  {
    text: 'Reports',
    icon: FileText,
    enabled: true,
    submenuLocation: '/reports',
    accountLevel: 2,
    permission: 'report:read',
    subRoutes: [
      {
        text: 'Time Worked',
        routeName: '/reports/time-worked',
        icon: Clock,
        enabled: true,
        permission: 'report:read',
        accountLevel: 2,
      },
      {
        text: 'Webcam Audit Results',
        routeName: '/reports/webcam-results',
        icon: Camera,
        enabled: true,
        permission: 'audit:webcam',
        accountLevel: 2,
      },
      {
        routeName: '/insiderThreats',
        text: 'Insider Threats',
        icon: ShieldAlert,
        enabled: true,
        permission: 'dashboard:insider-threats',
        accountLevel: 2,
      },
      {
        text: 'Timesheets',
        routeName: '/members/timesheets',
        icon: Clipboard,
        enabled: true,
        permission: 'timesheet:read:all',
        accountLevel: 2,
      },
      {
        text: 'Geolocation Results',
        routeName: '/reports/geo-results',
        icon: Map,
        enabled: true,
        permission: 'audit:geolocation',
        accountLevel: 2,
      },
      {
        text: "Team Member's Time Off",
        routeName: '/pto/members',
        icon: CalendarOff,
        enabled: true,
        permission: 'pto:read:team',
        accountLevel: 2,
      },
    ],
  },
  {
    text: 'Profile Update Requests',
    routeName: '/profile-update-requests',
    icon: UserCog,
    enabled: true,
    permission: 'profile:review',
    accountLevel: 2,
  },
  {
    text: 'Organization Chart',
    routeName: '/organization-chart',
    icon: Network,
    enabled: true,
    permission: 'organization:read',
    accountLevel: 2,
  },
  {
    text: 'Analytics',
    icon: BarChart3,
    enabled: true,
    submenuLocation: '/analytics',
    accountLevel: 2,
    subRoutes: [
      {
        text: 'Unusual Activity',
        routeName: '/analytics/unusual-activity',
        icon: AlertCircle,
        enabled: true,
      },
    ],
  },
  {
    routeName: '/geoDashboard',
    text: 'Geo Dashboard',
    icon: Map,
    enabled: true,
    permission: 'dashboard:geo',
    accountLevel: 2,
  },
  {
    text: 'Geo Map',
    routeName: '/geoMap',
    icon: Globe,
    enabled: true,
    permission: 'dashboard:geomap',
    accountLevel: 2,
  },
  {
    text: 'My Shoot',
    icon: UserCircle,
    submenuLocation: '/pto',
    subRoutes: [
      {
        text: 'Personal Time Off (PTO)',
        routeName: '/pto/requests',
        icon: Plane,
        enabled: true,
        permission: 'pto:create:own',
        accountLevel: 1,
      },
    ],
    enabled: true,
    permission: 'pto:read:own',
    accountLevel: 1,
  },
  {
    text: 'Manage Members',
    routeName: '/members/manage',
    icon: Users,
    enabled: true,
    permission: 'member:manage',
    accountLevel: 2,
  },
  {
    text: 'Settings & Permissions',
    icon: Settings,
    submenuLocation: '/settings',
    accountLevel: 2,
    permission: 'settings:read',
    subRoutes: [
      {
        text: 'Screenshots',
        routeName: '/members/screenshotFrequency',
        icon: ImageIcon,
        enabled: true,
        permission: 'settings:screenshots',
        accountLevel: 2,
      },
      {
        text: 'Webcam Audit',
        routeName: '/members/webcamAudit',
        icon: Webcam,
        enabled: true,
        permission: 'settings:webcam',
        accountLevel: 2,
      },
      {
        text: 'Team Management',
        routeName: '/admin/team-settings',
        icon: UsersRound,
        enabled: true,
        permission: 'settings:team',
        accountLevel: 2,
      },
      {
        text: 'All Settings',
        routeName: '/admin/settings',
        icon: Sliders,
        enabled: true,
        permission: 'settings:update:workspace',
        accountLevel: 2,
      },
    ],
    enabled: true,
  },
  {
    routeName: '/role-management',
    text: 'Role Management',
    icon: UserCog,
    enabled: true,
    accountLevel: 3,
    permission: 'workspace:manage',
  },
  {
    routeName: '/activity-logs',
    text: 'Activity Logs',
    icon: Activity,
    enabled: true,
    accountLevel: 3,
    permission: 'activity:read:all',
  },
  {
    routeName: '/help',
    text: 'Help & Docs',
    icon: BookOpen,
    enabled: true,
    accountLevel: 1,
  },
];

const linkArrayNamesTeamLeader = [
  {
    routeName: '/dashboard',
    text: 'Dashboard',
    icon: LayoutDashboard,
    enabled: true,
    permission: 'dashboard:read',
    accountLevel: 2,
  },
  {
    text: 'Team Engagement',
    routeName: '/members/team-engagement',
    icon: Activity,
    enabled: true,
    permission: 'team:read',
    accountLevel: 2,
  },
  chartAbstractionSubmenu,
  {
    text: 'Profile Update Requests',
    routeName: '/profile-update-requests',
    icon: UserCog,
    enabled: true,
    permission: 'profile:review',
    accountLevel: 2,
  },
  {
    text: 'Reports',
    icon: FileText,
    enabled: true,
    submenuLocation: '/reports',
    accountLevel: 2,
    permission: 'report:read',
    subRoutes: [
      {
        text: 'Timesheets',
        routeName: '/members/timesheets',
        icon: Clipboard,
        enabled: true,
        permission: 'timesheet:read:team',
        accountLevel: 2,
      },
    ],
  },
  {
    text: 'Time Off Requests',
    icon: Plane,
    submenuLocation: '/pto',
    permission: 'pto:read:own',
    accountLevel: 1,
    subRoutes: [
      {
        text: 'Personal Time Off (PTO)',
        routeName: '/pto/requests',
        icon: Plane,
        enabled: true,
        permission: 'pto:create:own',
        accountLevel: 1,
      },
      {
        text: "Team Member's Time Off",
        routeName: '/pto/members',
        icon: CalendarOff,
        enabled: true,
        permission: 'pto:read:team',
        accountLevel: 2,
      },
    ],
    enabled: true,
  },
  {
    routeName: '/help',
    text: 'Help & Docs',
    icon: BookOpen,
    enabled: true,
    accountLevel: 1,
  },
];

const linkArrayNamesMember = [
  {
    routeName: '/pto/requests',
    text: 'Time Off Requests',
    icon: Plane,
    enabled: true,
    permission: 'pto:create:own',
    accountLevel: 1,
  },
  chartAbstractionSubmenu,
  // {
  //   text: 'Reports',
  //   enabled: true,
  //   submenuLocation: '/reports',
  //   accountLevel: 2,
  //   subRoutes: [
  //     { text: 'Timesheets', routeName: '/members/timesheets', enabled: true },
  //   ],
  // },
  {
    routeName: '/members/activity/view',
    text: 'Timesheets',
    icon: Clipboard,
    enabled: true,
    accountLevel: 1,
  },
  {
    routeName: '/help',
    text: 'Help & Docs',
    icon: BookOpen,
    enabled: true,
    accountLevel: 1,
  },
];

const MenuLink = styled.li`
  ${(props) => !props.enabled && css`
    cursor: disabled;
    pointer-events: none;
    opacity: 0.3;
  `};
`;

function NavLeafItem({ linkInfo }) {
  const Icon = linkInfo.icon;
  // Entry-point-specific highlight: a page reached from elsewhere (e.g. a
  // member profile opened from Team Engagement) can keep its origin nav item
  // selected by linking with `?from=<routeName>`.
  const isActive = (match, loc) => (
    !!match || new URLSearchParams(loc?.search).get('from') === linkInfo.routeName
  );
  return (
    <MenuLink enabled={linkInfo.enabled} className="sidenav-leaf">
      <NavLink
        to={linkInfo.routeName}
        isActive={isActive}
        activeClassName="sidenav-link-active"
        className="sidenav-link"
        data-tour={linkInfo.routeName === '/help' ? 'nav-help' : undefined}
      >
        {Icon && <Icon size={16} className="sidenav-link-icon" />}
        <span className="sidenav-link-text">{linkInfo.text}</span>
      </NavLink>
    </MenuLink>
  );
}

NavLeafItem.propTypes = {
  linkInfo: PropTypes.shape({
    routeName: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    enabled: PropTypes.bool,
    icon: PropTypes.elementType,
  }).isRequired,
};

function NavGroupItem({
  linkInfo,
  member,
  open,
  onToggle,
}) {
  const location = useLocation();
  const history = useHistory();
  const filteredSubRoutes = linkInfo?.subRoutes?.filter((sr) => canAccessNavItem(sr, member)) || [];

  if (filteredSubRoutes.length === 0) return null;

  const Icon = linkInfo.icon;

  const handleHeaderClick = () => {
    const wasOpen = open;
    onToggle(linkInfo.text);
    if (!wasOpen && filteredSubRoutes[0]?.routeName) {
      history.push(filteredSubRoutes[0].routeName);
    }
  };

  return (
    <li className="sidenav-group">
      <button
        type="button"
        className="sidenav-link sidenav-group-trigger"
        onClick={handleHeaderClick}
      >
        {Icon && <Icon size={16} className="sidenav-link-icon" />}
        <span className="sidenav-link-text">{linkInfo.text}</span>
        <ChevronDown
          size={14}
          className={`sidenav-chevron ${open ? 'sidenav-chevron-open' : ''}`}
        />
      </button>
      {open && (
        <ul className="sidenav-submenu">
          {filteredSubRoutes.map((subRoute) => {
            // Entry-point referrer: a drilled-in page (e.g. a member's activity
            // opened from Reports → Timesheets) keeps its origin sub-route lit.
            const fromParam = new URLSearchParams(location?.search || '').get('from');
            const isActive = location?.pathname?.startsWith(subRoute.routeName)
              || fromParam === subRoute.routeName;
            return (
              <li key={subRoute.routeName} className="sidenav-leaf">
                <NavLink
                  to={subRoute.routeName}
                  className={`sidenav-link sidenav-sublink ${isActive ? 'sidenav-link-active' : ''}`}
                >
                  <span className="sidenav-link-text">{subRoute.text}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

NavGroupItem.propTypes = {
  linkInfo: PropTypes.shape({
    text: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    submenuLocation: PropTypes.string,
    subRoutes: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      routeName: PropTypes.string,
      icon: PropTypes.elementType,
      enabled: PropTypes.bool,
    })),
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  member: PropTypes.object,
  open: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

function Sidebar({ children }) {
  const { activeWorkspace, workspaces, member } = useContext(UserContext);
  const { handleWorkspaceSelection } = useWorkspaces();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onToggle = () => setMobileOpen((o) => !o);
    const onClose = () => setMobileOpen(false);
    window.addEventListener('shoot:toggle-sidenav', onToggle);
    window.addEventListener('shoot:close-sidenav', onClose);
    return () => {
      window.removeEventListener('shoot:toggle-sidenav', onToggle);
      window.removeEventListener('shoot:close-sidenav', onClose);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location?.pathname]);

  // Chart Abstraction nav gating: hidden until the workspace flag confirms it.
  const [caStatus, setCaStatus] = useState({ enabled: false, qaReview: false });
  useEffect(() => {
    if (!activeWorkspace?.id) return undefined;
    let active = true;
    (async () => {
      const res = await sendRequest({
        method: 'get',
        url: API.CHART_ABSTRACTION.status(activeWorkspace.id),
      });
      if (active && res.success) {
        setCaStatus({ enabled: !!res.data.enabled, qaReview: !!res.data.qaReview });
      }
    })();
    return () => { active = false; };
  }, [activeWorkspace?.id]);

  if (member.isReportingManager && !linkArrayNamesMember.some((link) => link.routeName === '/dashboard')) {
    linkArrayNamesMember.unshift({
      routeName: '/dashboard',
      text: 'Dashboard',
      icon: LayoutDashboard,
      enabled: true,
      permission: 'workspace:read',
    });
  }

  const memberLevel = getMemberRoleLevel(member);
  // eslint-disable-next-line no-nested-ternary
  const linkArray = memberLevel >= accountTypeLevel['Workspace admin']
    ? linkArrayNamesAdmin
    : (memberLevel === accountTypeLevel['Team Leader'] || member.isReportingManager)
      ? linkArrayNamesTeamLeader
      : linkArrayNamesMember;

  // Filter nav items based on permissions
  const filteredLinkArray = linkArray
    .filter((linkInfo) => canAccessNavItem(linkInfo, member))
    // The "Chart Abstraction" section shows when the feature is enabled AND the
    // member can use it: Team Leader+ (admins) OR anyone with QA Review access.
    // QA reviewers thus get the full section (Overview, Team Productivity, QA Queue).
    .filter((linkInfo) => {
      if (linkInfo.submenuLocation === '/admin/chart-abstraction') {
        const isAdminLevel = memberLevel >= accountTypeLevel['Team Leader'];
        return caStatus.enabled && (isAdminLevel || caStatus.qaReview);
      }
      return true;
    });

  useEffect(() => {
    const fromParam = new URLSearchParams(location?.search || '').get('from');
    const matchingGroup = filteredLinkArray.find((item) => {
      if (!item.subRoutes) return false;
      if (item.submenuLocation && location?.pathname?.startsWith(item.submenuLocation)) {
        return true;
      }
      return item.subRoutes.some((sr) => sr.routeName
        && (location?.pathname?.startsWith(sr.routeName) || fromParam === sr.routeName));
    });
    if (matchingGroup && matchingGroup.text !== openGroup) {
      setOpenGroup(matchingGroup.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname, location?.search]);

  const handleToggleGroup = (groupText) => {
    setOpenGroup((current) => (current === groupText ? null : groupText));
  };

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="sidenav-backdrop"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside className={`sidenav ${mobileOpen ? 'sidenav-open' : ''}`}>
        <nav className="sidenav-nav" data-tour="nav">
          <ul className="sidenav-list">
            {filteredLinkArray.map((linkInfo) => (linkInfo?.subRoutes
              ? (
                <NavGroupItem
                  key={linkInfo.text}
                  linkInfo={linkInfo}
                  member={member}
                  open={openGroup === linkInfo.text}
                  onToggle={handleToggleGroup}
                />
              )
              : (
                <NavLeafItem
                  key={linkInfo.routeName}
                  linkInfo={linkInfo}
                />
              )))}
          </ul>
        </nav>

        <WorkspaceSwitcher
          activeWorkspace={activeWorkspace}
          workspaces={workspaces}
          onSelect={handleWorkspaceSelection}
        />
      </aside>
      {children}
    </>
  );
}

function WorkspaceSwitcher({ activeWorkspace, workspaces, onSelect }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const others = (workspaces || []).filter((w) => w.id !== activeWorkspace?.id);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div className="sidenav-workspace" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="sidenav-workspace-button"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Avatar
          className="sidenav-workspace-avatar"
          src={activeWorkspace?.picture || WorkspaceSVG}
        />
        <div className="sidenav-workspace-info">
          <p className="sidenav-workspace-label">Workspace</p>
          <p className="sidenav-workspace-name">{activeWorkspace?.name}</p>
        </div>
        <ChevronDown
          size={14}
          className={`sidenav-chevron ${open ? 'sidenav-chevron-open' : ''}`}
        />
      </button>
      {open && (
        <div className="sidenav-workspace-popover" role="listbox">
          {others.length > 0 ? others.map((ws) => (
            <button
              key={ws.id || ws.name}
              type="button"
              onClick={() => {
                onSelect(ws);
                setOpen(false);
              }}
              className="sidenav-workspace-option"
              role="option"
              aria-selected="false"
            >
              <span
                className="sidenav-workspace-option-avatar"
                style={{
                  backgroundImage: ws.picture ? `url(${ws.picture})` : undefined,
                  backgroundColor: ws.picture ? undefined : '#ececf0',
                }}
              >
                {!ws.picture && (ws.name?.[0] || '?').toUpperCase()}
              </span>
              <span className="sidenav-workspace-option-name">{ws.name}</span>
            </button>
          )) : (
            <p className="sidenav-workspace-empty">No other workspaces</p>
          )}
        </div>
      )}
    </div>
  );
}

WorkspaceSwitcher.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  activeWorkspace: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  workspaces: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
};

export { MenuLink, Sidebar };

Sidebar.propTypes = {
  children: PropTypes.elementType,
};
